/**
 * Pure Tribe - Ambassador Tracking & Discount Suppression for Halloween Promo
 *
 * 1. Checks if customer lands with an ambassador discount code (e.g., /discount/ALEXA10, ?discount=ALEXA10, or ?ambassador=ALEXA10).
 * 2. If the code is in the ambassador whitelist:
 *    - Persists the code in localStorage / sessionStorage.
 *    - Suppresses/clears the native Shopify `discount_code` cookie so the 10% discount is not applied at checkout.
 * 3. Syncs the code to Shopify Cart Note Attributes ('Ambassador Code') via `/cart/update.js` so attribution remains on the order.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'pt_ambassador_code';
  const ATTRIBUTE_KEY = 'Ambassador Code';

  // Normalized (uppercase) set of ambassador codes
  const AMBASSADOR_CODES = new Set([
    'ADRIANA',
    'ALDONZA10',
    'ALEXA10',
    'ALONDRA',
    'ALONSOVGQ',
    'ANABREU',
    'ANALISSA',
    'ANDYP',
    'ANGELICAGR',
    'ANTO10',
    'ANZO',
    'BALANCED',
    'BELINDA10',
    'BERNA10',
    'BETZY10',
    'BOLDFITHAPPY17',
    'BRENDA10',
    'BRENDAFRAUN',
    'BRENFLOWERS',
    'BURNWAIMEE',
    'BYALE',
    'BYMARTHAGEREZ',
    'CANETRIBE',
    'CECI10',
    'CELLCATT',
    'CHATA10',
    'CHELO10',
    'CRISTINA',
    'DANI10',
    'DANIBARRE',
    'DANIYOGUI',
    'DIANA10',
    'DOLLY10',
    'DRANICO',
    'DULCINA',
    'EATSBYNATH',
    'EDSANUTRI',
    'EMMAPINTO',
    'FCURIOSA',
    'FERGODOY',
    'FITWIN',
    'FRANCISCOVFIT',
    'GLADYS',
    'GLOTURRUCO',
    'GOZA',
    'GUTHEALTH',
    'HEALTHYCORDE',
    'IAMSANDRA',
    'ISA10',
    'ISABEL10',
    'JESSI10',
    'JESSICA10',
    'JHOVANA',
    'JUDITHGONZALEZ',
    'JULY10',
    'KARIME10',
    'KARINA10',
    'KARLA10',
    'KARLAM',
    'KARLI10OFF',
    'KATHYTRIBE',
    'KATYNUTRI',
    'LACAMBON',
    'LADYROBOTINA',
    'LATINA',
    'LIFEWITHDENISSE',
    'LIZ10',
    'LUCIAVILLARREAL',
    'LUCY',
    'LUPITIPS',
    'LUZ10',
    'MACA10',
    'MARA10',
    'MARAGUILAR',
    'MARCANAVATI',
    'MARCEGUERRA',
    'MARIA10',
    'MARIANAC',
    'MARIANAGODOY',
    'MARIECHEN',
    'MARIELA_CONTE23',
    'MARU10',
    'MARYBEL10',
    'MELO',
    'MELODIJOLANUTRI',
    'MERCEDES',
    'MERCHE',
    'MILES',
    'MMF',
    'MON10',
    'MONWTRIBE',
    'NAOMI10',
    'NAT10',
    'NATSCOCINA',
    'NATZEN',
    'NINA10',
    'NUTREPAULA',
    'NUTREYMEDITA',
    'NUTRIBREN',
    'NUTRILAURAO',
    'NUTRIMINGO',
    'NUTRIVAL',
    'NUTRIVALERIA',
    'OZK10',
    'PAU10',
    'PAULI10',
    'PAUMILLER',
    'PAUTERMINEL',
    'PILATESSNOB',
    'PILY10',
    'PROTRAINING',
    'RAZ',
    'REN10',
    'REVOLVE10',
    'RIZZO10',
    'SALUDFUNCIONAL',
    'SANDRA10',
    'SANTAELLA',
    'SARQUIZ10',
    'SILVIA10',
    'SILVIANUTRICION',
    'SONY',
    'SOYJOS',
    'SUSY10',
    'THEBURN',
    'TIFFANY10',
    'TRIVIHEALTH',
    'TROFISOUL',
    'VALERIA',
    'VALERIASENDEL',
    'VALERIAVS',
    'VAR10',
    'YSABEL',
    'ZMEAL10',
    'VANEDAMM',
    'ANACANEPA',
    // Testing code
    'TESTAMBASSADOR'
  ]);

  /**
   * Clears the native Shopify discount cookie across common paths and domains
   */
  function suppressDiscountCookie() {
    const pastDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const host = window.location.hostname;
    const domains = [
      '',
      `; domain=${host}`,
      `; domain=.${host}`,
      `; domain=${host.split('.').slice(-2).join('.')}`
    ];

    domains.forEach(d => {
      document.cookie = `discount_code=; path=/; expires=${pastDate}${d}`;
      document.cookie = `discount_code=; path=/checkout; expires=${pastDate}${d}`;
    });
  }

  /**
   * Reads a cookie value by name
   */
  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return match ? decodeURIComponent(match[3]) : null;
  }

  /**
   * Extracts potential discount/ambassador code from URL or Shopify's redirect cookie
   */
  function extractCode() {
    try {
      const url = new URL(window.location.href);

      // 1. Check path: /discount/CODE
      const pathMatch = url.pathname.match(/\/discount\/([^/?#]+)/i);
      if (pathMatch && pathMatch[1]) {
        return decodeURIComponent(pathMatch[1]).trim().toUpperCase();
      }

      // 2. Check query params: ?discount=CODE or ?ambassador=CODE
      const paramCode = url.searchParams.get('discount') || url.searchParams.get('ambassador');
      if (paramCode) {
        return decodeURIComponent(paramCode).trim().toUpperCase();
      }

      // 3. Check discount_code cookie set by Shopify server on /discount/CODE 302 redirect
      const cookieDiscount = getCookie('discount_code');
      if (cookieDiscount) {
        return cookieDiscount.trim().toUpperCase();
      }
    } catch (e) {
      // In case URL or cookie parsing fails
    }
    return null;
  }

  /**
   * Syncs the ambassador code to the Shopify Cart attribute via AJAX
   */
  function syncCodeToCart(code, callback) {
    if (!code) return;

    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        // Only update if not already set to this code
        if (cart && cart.attributes && cart.attributes[ATTRIBUTE_KEY] === code) {
          if (typeof callback === 'function') callback(cart);
          return;
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              [ATTRIBUTE_KEY]: code
            }
          })
        })
          .then(res => res.json())
          .then(updatedCart => {
            if (typeof callback === 'function') callback(updatedCart);
          });
      })
      .catch(err => {
        console.warn('[PureTribe] Failed to sync ambassador attribute to cart:', err);
      });
  }

  /**
   * Removes the ambassador code from storage and cart attribute
   */
  function removeAmbassadorCode(callback) {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          [ATTRIBUTE_KEY]: ''
        }
      })
    })
      .then(res => res.json())
      .then(updatedCart => {
        if (typeof callback === 'function') callback(updatedCart);
      })
      .catch(err => {
        console.warn('[PureTribe] Failed to remove ambassador attribute from cart:', err);
      });
  }

  /**
   * Dispatches theme cart update events to refresh the drawer UI
   */
  function triggerCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /**
   * Sets up interception for manual ambassador code entry in the drawer discount form
   */
  function hideDiscountErrors(container) {
    const parent = container || document;
    parent.querySelectorAll('.cart-discount__error').forEach(el => {
      el.classList.add('hidden');
      el.style.setProperty('display', 'none', 'important');
    });
    parent.querySelectorAll('.cart-discount__error-text').forEach(el => {
      el.classList.add('hidden');
      el.style.setProperty('display', 'none', 'important');
    });
  }

  /**
   * Sets up interception for manual ambassador code entry in the drawer discount form
   */
  function setupManualInputInterception() {
    function processAmbassadorInput(form, input) {
      if (!form || !input) return false;
      const enteredCode = (input.value || '').trim().toUpperCase();
      if (!enteredCode || !AMBASSADOR_CODES.has(enteredCode)) return false;

      try {
        localStorage.setItem(STORAGE_KEY, enteredCode);
        sessionStorage.setItem(STORAGE_KEY, enteredCode);
      } catch (e) {}

      suppressDiscountCookie();
      input.value = '';

      const discountComponent = form.closest('cart-discount-component');
      if (discountComponent) {
        discountComponent.setAttribute('data-has-ambassador', 'true');
        hideDiscountErrors(discountComponent);
      } else {
        hideDiscountErrors();
      }

      // Sync attribute to cart and trigger drawer UI update
      syncCodeToCart(enteredCode, () => {
        triggerCartRefresh();
        setTimeout(hideDiscountErrors, 50);
        setTimeout(hideDiscountErrors, 300);
      });

      return true;
    }

    // Intercept discount form submission
    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form || !form.matches('.cart-discount__form, form[on\\:submit="/applyDiscount"]')) return;

      const input = form.querySelector('input[name="discount"], #cart-discount');
      if (processAmbassadorInput(form, input)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    // Intercept click on the apply button directly
    document.addEventListener('click', function (event) {
      const applyBtn = event.target.closest('.cart-discount__button');
      if (!applyBtn) return;

      const form = applyBtn.closest('form');
      const input = form ? form.querySelector('input[name="discount"], #cart-discount') : document.getElementById('cart-discount');
      if (processAmbassadorInput(form, input)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    // Intercept Enter key inside the discount input
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      const input = event.target;
      if (!input || !input.matches('#cart-discount, .cart-discount__input')) return;

      const form = input.closest('form');
      if (processAmbassadorInput(form, input)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    // Intercept click on the discount removal button if it is for an ambassador code
    document.addEventListener('click', function (event) {
      const removeBtn = event.target.closest('.cart-discount__pill-remove');
      if (!removeBtn) return;

      const pill = removeBtn.closest('.cart-discount__pill');
      const code = (pill?.dataset?.discountCode || pill?.querySelector('.cart-discount__pill-code')?.textContent || '').trim().toUpperCase();

      let storedCode = null;
      try {
        storedCode = (localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY) || '').trim().toUpperCase();
      } catch (e) {}

      if (code && (code === storedCode || AMBASSADOR_CODES.has(code))) {
        event.preventDefault();
        event.stopImmediatePropagation();

        removeAmbassadorCode(() => {
          pill.remove();
          triggerCartRefresh();
        });
      }
    }, true);
  }

  // --- Main Execution ---
  const incomingCode = extractCode();

  if (incomingCode && AMBASSADOR_CODES.has(incomingCode)) {
    try {
      localStorage.setItem(STORAGE_KEY, incomingCode);
      sessionStorage.setItem(STORAGE_KEY, incomingCode);
    } catch (e) {}

    // Clear Shopify's discount cookie immediately and after brief delays
    suppressDiscountCookie();
    setTimeout(suppressDiscountCookie, 300);
    setTimeout(suppressDiscountCookie, 1000);
  }

  // Check stored ambassador code
  let activeCode = null;
  try {
    activeCode = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
  } catch (e) {}

  if (activeCode && AMBASSADOR_CODES.has(activeCode)) {
    // Keep discount cookie suppressed on all pages
    suppressDiscountCookie();

    // Sync to cart
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => syncCodeToCart(activeCode));
    } else {
      syncCodeToCart(activeCode);
    }

    // Re-check cookie suppression and attribute sync on cart/navigation events
    window.addEventListener('focus', suppressDiscountCookie);
    document.addEventListener('cart:update', () => syncCodeToCart(activeCode));
    document.addEventListener('cart:item-added', () => syncCodeToCart(activeCode));
  }

  // Initialize drawer manual input interception
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupManualInputInterception);
  } else {
    setupManualInputInterception();
  }
})();
 